import { CloseButton, Dialog, Portal } from '@chakra-ui/react';
import { ReactNode, useContext } from 'react';

import VButton from './VButton.jsx';
import { PortalContainerContext } from './providers/chakra';

const CLOSE_BUTTON_CLEARANCE = '14';

type VButtonColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive'
  | 'destructive-outline'
  | 'destructive-ghost'
  | 'neutral'
  | 'plain';

type DataAttributes = { [key in `data-${string}`]?: string };

type VDialogPWProps = Omit<Dialog.RootProps, 'children'> & {
  title?: ReactNode;
  body?: ReactNode;
  loading?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
  saveBtnText?: string;
  cancelBtnText?: string;
  saveButtonColor?: VButtonColor;
  showCloseButton?: boolean;
  headerProps?: Dialog.HeaderProps & DataAttributes;
  contentProps?: Dialog.ContentProps & DataAttributes;
  showSaveButton?: boolean;
  showCancelButton?: boolean;
  saveButtonDisabled?: boolean;
};

const VDialogPW = ({
  title,
  body,
  open,
  loading,
  onOpenChange,
  onOk,
  onCancel,
  saveBtnText = 'Save',
  cancelBtnText = 'Cancel',
  placement = 'center',
  saveButtonColor = 'primary',
  showCloseButton = true,
  headerProps = undefined,
  contentProps = undefined,
  size = 'md',
  showSaveButton = true,
  showCancelButton = true,
  saveButtonDisabled = false,
  ...props
}: VDialogPWProps) => {
  const portalContainerRef = useContext(PortalContainerContext);

  return (
    <Dialog.Root
      {...props}
      lazyMount
      size={size}
      placement={placement}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Portal container={portalContainerRef ?? undefined}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content {...contentProps}>
            <Dialog.Header
              pr={showCloseButton ? CLOSE_BUTTON_CLEARANCE : undefined}
              {...headerProps}
            >
              <Dialog.Title flex="1">{title}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body overflow="auto">{body}</Dialog.Body>
            <Dialog.Footer>
              {showCancelButton && (
                <Dialog.ActionTrigger asChild>
                  <VButton color="secondary" onClick={onCancel}>
                    {cancelBtnText}
                  </VButton>
                </Dialog.ActionTrigger>
              )}
              {showSaveButton && (
                <VButton
                  working={loading}
                  disabled={saveButtonDisabled}
                  onClick={onOk}
                  color={saveButtonColor}
                >
                  {saveBtnText}
                </VButton>
              )}
            </Dialog.Footer>
            {showCloseButton && (
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default VDialogPW;
